import React from 'react';
import { useSelector } from 'react-redux';
import siteSetting from '../../config/env';
import toast from './Toast';
import './shareLinks.css';

// Shareable public deep links to an individual service / product / voucher.
// Salon staff paste these into their own marketing so a client lands straight
// on that item in the public booking/shop site.
//
// Attribution mirrors settings/BookingUrl.js:
//   service  -> the staff member who creates the link (their professional page)
//   product  -> salon-level (self-employed don't sell products online)
//   voucher  -> salon-level gift-card shop

const WEB = (siteSetting && siteSetting.api && siteSetting.api.weburl) || '/';

// Salon slug + current staff id from the logged-in user (same source/fallbacks
// as BookingUrl.js — slug falls back to the salon _id, staff to userId || _id).
export const getShareContext = (hairdresser) => {
  const hd = hairdresser || {};
  const salon = hd.salon || {};
  return {
    salonSlug: salon.slug || salon._id || '',
    hairdresserId: hd.userId || hd._id || '',
  };
};

export const buildShareUrl = ({ type, id, salonSlug, hairdresserId }) => {
  if (!salonSlug || !id) return '';
  const base = `${WEB}book/${salonSlug}`;
  switch (type) {
    case 'service':
      return hairdresserId
        ? `${base}/professional/${hairdresserId}?service=${id}`
        : `${base}?service=${id}`;
    case 'product':
      return `${base}/shop?product=${id}`;
    case 'voucher':
      return `${base}/gift-cards?voucher=${id}`;
    default:
      return base;
  }
};

export const copyShareUrl = (url) => {
  if (!url) { toast.error('Could not build link'); return Promise.resolve(false); }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(url)
      .then(() => { toast.success('Link copied'); return true; })
      .catch(() => { toast.error('Could not copy link'); return false; });
  }
  // Fallback for older / non-secure contexts.
  try {
    const ta = document.createElement('textarea');
    ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand('copy'); document.body.removeChild(ta);
    toast.success('Link copied');
    return Promise.resolve(true);
  } catch (e) {
    toast.error('Could not copy link');
    return Promise.resolve(false);
  }
};

const LinkIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.71 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

// Small icon button that copies a share link. Stops propagation so it never
// triggers the surrounding clickable row/card. Pulls the salon/staff context
// from redux so callers only need `type` + `id` (overridable via props).
const CopyLinkButton = ({ type, id, salonSlug, hairdresserId, className = '', title = 'Copy link' }) => {
  const hairdresser = useSelector((state) => (state.user && state.user.userInfo && state.user.userInfo.hairdresser) || null);
  const ctx = getShareContext(hairdresser);
  const slug = salonSlug || ctx.salonSlug;
  const hdId = hairdresserId || ctx.hairdresserId;

  const onClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    copyShareUrl(buildShareUrl({ type, id, salonSlug: slug, hairdresserId: hdId }));
  };

  return (
    <button type="button" className={`share-link-btn ${className}`} onClick={onClick} title={title} aria-label={title}>
      <LinkIcon />
    </button>
  );
};

export default CopyLinkButton;
